import React, { useState } from "react";
import { Drawer, Modal, Textarea, Group } from "@mantine/core";
import moment from "moment";
import Icon from "./icons";
import { updateAttendenceStaff } from "../../../services/staff";
import { fmtRange } from "./scheduleUtils";
import css from "./Scheduler.module.css";

const initials = (name) =>
  (name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";
const fmtT = (v) => (v ? moment(v).format("h:mm A") : null);
const fmtDateLong = (v) => (v ? moment(v).format("dddd, MMM D") : "");
const dateOf = (t) => t?.date?.date || t?.startTime;
const idOf = (t) => t?._id || t?.id;

// Per-day EVV / time-tracking detail (clock-in / clock-out cards) shown when a
// daily entry row is expanded. "Edit EVV" opens the shared EVV record editor.
function DayEvv({ track, onEditEvv }) {
  const gIn = track.evvClockIn?.location;
  const evvOk = !!(gIn && gIn.lat != null);
  const hasEvv = !!(track.evvClockIn || track.evvClockOut);

  const card = (label, dotClass, time, evv) => {
    const loc = evv?.location;
    return (
      <div className={css.evvCard}>
        <div className={css.evvCardH}>
          <span className={`${css.evvDot} ${dotClass}`} />
          <span className={css.evvCardLbl}>{label}</span>
          <span className={css.evvCardTime}>{time || "—"}</span>
        </div>
        <div className={css.evvKv}><span>Coordinates</span><b className={css.mono}>{loc && loc.lat != null ? `${loc.lat}, ${loc.lng}` : "—"}</b></div>
        <div className={css.evvKv}><span>Service address</span><b>{evv?.serviceAddress || "—"}</b></div>
        {evv?.distanceToSite != null ? <div className={css.evvKv}><span>Distance</span><b>{Number(evv.distanceToSite).toFixed(2)} mi</b></div> : null}
        {evv?.deviceInfo?.deviceType ? <div className={css.evvKv}><span>Device</span><b style={{ textTransform: "capitalize" }}>{evv.deviceInfo.deviceType}</b></div> : null}
      </div>
    );
  };

  return (
    <div className={css.evvDetail}>
      <div className={css.evvHead}>
        <div className={css.evvBadges}>
          {evvOk
            ? <span className={`${css.badge2} ${css.green}`}><Icon name="check" size={11} /> GPS verified</span>
            : <span className={`${css.badge2} ${css.grey}`}>No GPS recorded</span>}
          {track.service ? <span className={`${css.badge2} ${css.blue}`}>{track.service}</span> : null}
          {track.manualEntry ? <span className={`${css.badge2} ${css.violet}`}><Icon name="pin" size={11} /> Manual entry</span> : null}
          <span className={`${css.badge2} ${track.evvEnforced ? css.green : css.grey}`}>{track.evvEnforced ? "EVV enforced" : "Not enforced"}</span>
        </div>
        {onEditEvv && hasEvv ? (
          <button type="button" className={css.evvEditBtn} onClick={() => onEditEvv(track)}>
            <Icon name="edit" size={13} /> Edit EVV
          </button>
        ) : null}
      </div>
      {track.manualEntry ? (
        <div className={css.evvKv} style={{ marginBottom: 10 }}>
          <span>Reason</span><b>{track.manualEntryReason || "No reason given"}</b>
        </div>
      ) : null}
      <div className={css.evvCols}>
        {card("Clock-in", css.evvDotIn, fmtT(track.startTime), track.evvClockIn)}
        {card("Clock-out", css.evvDotOut, fmtT(track.endTime), track.evvClockOut)}
      </div>
    </div>
  );
}

// Prototype-faithful timesheet detail drawer (openTimesheetDrawer): staff
// summary, attendance exceptions, expandable daily entries with EVV, and an
// Add Missed Time / Approve Timesheet footer. Driven by real userTracking data.
// minutes-from-midnight -> "HH:mm" for prefilling the manual-entry time fields.
const mmToHHMM = (m) => {
  const x = (((m || 0) % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
};

export default function TimesheetDetailDrawer({ opened, row, weekLabel, onClose, onApproved, onAddMissed, onAddMissedForShift, onEditEntry, onEditEvv, showOvertime = true, otThreshold = 40 }) {
  const [expanded, setExpanded] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  if (!row) return null;

  const tracks = (row.tracks || []).slice().sort((a, b) => new Date(dateOf(a)) - new Date(dateOf(b)));
  const logged = tracks.reduce((a, t) => a + (Number(t.total_work_time) || 0), 0) / 3600;
  const ot = showOvertime ? Math.max(0, logged - otThreshold) : 0;
  const regular = Math.min(otThreshold, logged);
  // Hand-keyed "missed time" hours inside this timesheet, surfaced so the
  // approver can see how much of the total was never actually clocked.
  const manualTracks = tracks.filter((t) => t.manualEntry === true);
  const manualHours = manualTracks.reduce((a, t) => a + (Number(t.total_work_time) || 0), 0) / 3600;
  const missingOut = tracks.filter((t) => !t.endTime);
  const lateEntries = tracks.filter((t) => t.late);
  const missedShifts = row.missed || [];
  const hasExceptions = missingOut.length || lateEntries.length || missedShifts.length;
  const schedRange = (s) => (s && s.startMin != null && s.endMin != null ? fmtRange(s.startMin, s.endMin) : null);
  const pending = row.pending || tracks.filter((t) => t.approvedStatus !== "approved");
  const rejectedReason = (tracks.find((t) => t.approvedStatus === "rejected" && t.rejectionReason) || {}).rejectionReason;

  const approveAll = async () => {
    const payload = pending.map((t) => ({ id: idOf(t), approvedStatus: "approved" }));
    if (!payload.length) { onClose?.(); return; }
    setApproving(true);
    await updateAttendenceStaff(payload);
    setApproving(false);
    onApproved?.();
    onClose?.();
  };

  const rejectAll = async () => {
    const payload = pending.map((t) => ({ id: idOf(t), approvedStatus: "rejected", rejectionReason: reason.trim() }));
    if (!payload.length) { setRejecting(false); return; }
    setApproving(true);
    await updateAttendenceStaff(payload);
    setApproving(false);
    setRejecting(false);
    setReason("");
    onApproved?.();
    onClose?.();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={620}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      overlayProps={{ opacity: 0.55, blur: 3 }}
      zIndex={1100}
    >
      <div className={css.dwWrap}>
        <div className={css.dwHead}>
          <div>
            <h3 className={css.dwTitle}>{row.name}</h3>
            <div className={css.dwSub}>Timesheet · {weekLabel}</div>
          </div>
          <button type="button" className={css.dwClose} onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className={css.dwBody}>
          {/* Summary */}
          <div className={`${css.tsCard} ${css.tsSummary}`}>
            <span className={css.tsAv}>{row.photo ? <img src={row.photo} alt="" /> : initials(row.name)}</span>
            <div style={{ flex: 1 }}>
              <div className={css.tsNm}>{row.name}</div>
              <div className={css.tsRole}>{row.role || "Caregiver"}</div>
            </div>
            <div className={css.tsHours}>
              <div className={css.tsHoursNum}>{logged.toFixed(1)}h</div>
              <div className={css.tsHoursLbl}>logged</div>
            </div>
          </div>

          {/* Hours breakdown — shown when there's overtime against the threshold
              or any manually entered time to call out. */}
          {ot > 0 || manualHours > 0 ? (
            <div className={css.tsCard} style={{ display: "flex", padding: "12px 18px", marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--app-text-muted)" }}>Regular</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "var(--app-text)", marginTop: 2 }}>{regular.toFixed(1)}h</div>
              </div>
              {ot > 0 ? (
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#b45309" }}>Overtime</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#b45309", marginTop: 2 }}>{ot.toFixed(1)}h</div>
                </div>
              ) : null}
              {manualHours > 0 ? (
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#5925DC" }}>Manual</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#5925DC", marginTop: 2 }}>{manualHours.toFixed(1)}h</div>
                </div>
              ) : null}
              <div style={{ textAlign: "right", alignSelf: "center" }}>
                <div style={{ fontSize: 11.5, color: "var(--app-text-muted)" }}>
                  {ot > 0 ? `over ${otThreshold}h / week` : `${manualTracks.length} manual ${manualTracks.length === 1 ? "entry" : "entries"}`}
                </div>
              </div>
            </div>
          ) : null}

          {rejectedReason ? (
            <div className={css.excClear} style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#b42318", marginBottom: 14 }}>
              <Icon name="alert" size={15} /> <span><strong>Previously rejected:</strong> {rejectedReason}</span>
            </div>
          ) : null}

          {/* Attendance exceptions — missing clock-out, late clock-in, and
              missed (no-show) shifts, compared against the schedule. */}
          <div className={`${css.tsCard} ${css.psec}`}>
            <h5 className={css.psecH}>Attendance Exceptions</h5>
            {hasExceptions ? (
              <div className={css.excList}>
                {missedShifts.map((s) => {
                  const refId = (v) => (v && (v._id || v.id)) || v || "";
                  const prefill = {
                    staffId: row.staffId,
                    type: s.type === "facility" ? "facility" : "client",
                    facilityId: refId(s.facility),
                    clientId: refId(s.client),
                    services: s.service ? [s.service] : Array.isArray(s.services) ? s.services : [],
                    date: s.date ? new Date(s.date) : new Date(),
                    startTime: s.startMin != null ? mmToHHMM(s.startMin) : "",
                    endTime: s.endMin != null ? mmToHHMM(s.endMin) : "",
                  };
                  return (
                    <div key={`missed-${s._id || s.id}`} className={css.excLine}>
                      <span className={`${css.badge2} ${css.red}`}><Icon name="alert" size={11} /> Missed shift</span>
                      <div className={css.excMeta}>
                        <div className={css.excD}>{fmtDateLong(s.date)}</div>
                        <div className={css.excS}>{[s.service, schedRange(s) && `scheduled ${schedRange(s)}`, "no clock-in"].filter(Boolean).join(" · ")}</div>
                      </div>
                      <div className={css.excActs}>
                        <button type="button" className={`${css.btnXs} ${css.btnSoft}`} onClick={() => onAddMissedForShift?.(prefill)}>Add entry</button>
                      </div>
                    </div>
                  );
                })}
                {lateEntries.map((t) => (
                  <div key={`late-${idOf(t)}`} className={css.excLine}>
                    <span className={`${css.badge2} ${css.amber}`}><Icon name="alert" size={11} /> Late clock-in</span>
                    <div className={css.excMeta}>
                      <div className={css.excD}>{fmtDateLong(dateOf(t))}</div>
                      <div className={css.excS}>{[schedRange(t.shift) && `scheduled ${schedRange(t.shift)}`, fmtT(t.startTime) && `clocked in ${fmtT(t.startTime)}`].filter(Boolean).join(" · ")}</div>
                    </div>
                    <div className={css.excActs}>
                      <button type="button" className={`${css.btnXs} ${css.btnSoft}`} onClick={() => onEditEntry?.(t)}>Edit time</button>
                    </div>
                  </div>
                ))}
                {missingOut.map((t) => (
                  <div key={`open-${idOf(t)}`} className={css.excLine}>
                    <span className={`${css.badge2} ${css.amber}`}><Icon name="alert" size={11} /> Missing clock-out</span>
                    <div className={css.excMeta}>
                      <div className={css.excD}>{fmtDateLong(dateOf(t))}</div>
                      <div className={css.excS}>{[t.service, fmtT(t.startTime) && `in ${fmtT(t.startTime)}`].filter(Boolean).join(" · ")}</div>
                    </div>
                    <div className={css.excActs}>
                      <button type="button" className={`${css.btnXs} ${css.btnSoft}`} onClick={() => onEditEntry?.(t)}>Edit time</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={css.excClear}><Icon name="check" size={15} /> No exceptions. Every scheduled shift was clocked in and out on time.</div>
            )}
          </div>

          {/* Daily entries */}
          <div className={css.tsCard}>
            <div className={css.entriesH}><h5 className={css.psecH} style={{ margin: 0 }}>Daily Entries</h5></div>
            <div className={css.tblScroll}>
              <table className={css.tbl} style={{ minWidth: 0 }}>
                <thead>
                  <tr>
                    <th className={css.deCc} />
                    <th>Date</th>
                    <th>Scheduled</th>
                    <th>In</th>
                    <th>Out</th>
                    <th>Hours</th>
                    <th style={{ textAlign: "right" }} />
                  </tr>
                </thead>
                <tbody>
                  {tracks.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--app-text-muted)", padding: 14 }}>No entries</td></tr>
                  ) : (
                    tracks.map((t) => {
                      const id = idOf(t);
                      const open = expanded === id;
                      const hrs = (Number(t.total_work_time) || 0) / 3600;
                      return (
                        <React.Fragment key={id}>
                          <tr className={`${css.deMain} ${open ? css.deOpen : ""}`} onClick={() => setExpanded(open ? null : id)}>
                            <td className={css.deCc}><span className={css.deChev} style={{ transform: open ? "rotate(90deg)" : "none" }}><Icon name="chevron" size={13} /></span></td>
                            <td className={css.deDate}>{fmtDateLong(dateOf(t))}</td>
                            <td className={css.mono}>{schedRange(t.shift) || <span style={{ color: "var(--app-text-muted)" }}>—</span>}</td>
                            <td className={css.mono}>
                              {fmtT(t.startTime) || "—"}
                              {t.late ? <span className={`${css.badge2} ${css.amber}`} style={{ marginLeft: 6 }}>Late</span> : null}
                              {t.manualEntry ? <span className={`${css.badge2} ${css.violet}`} style={{ marginLeft: 6 }} title={t.manualEntryReason || "Manually added"}>Manual</span> : null}
                            </td>
                            <td className={css.mono}>{t.endTime ? fmtT(t.endTime) : <span className={`${css.badge2} ${css.amber}`}>open</span>}</td>
                            <td className={css.numStrong}>{hrs.toFixed(2)}h</td>
                            <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                              <button type="button" className={`${css.btnXs} ${css.btnSoft}`} onClick={() => onEditEntry?.(t)}>Edit</button>
                            </td>
                          </tr>
                          {open ? (
                            <tr>
                              <td className={css.deDetailTd} colSpan={7}><DayEvv track={t} onEditEvv={onEditEvv} /></td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className={css.dwFoot}>
          <button type="button" className={css.btnG} onClick={() => onAddMissed?.()}>
            <Icon name="plus" size={16} /> Add Missed Time
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className={css.btnG} onClick={() => setRejecting(true)} disabled={approving || !pending.length} style={{ color: "#b42318", borderColor: "#fecaca" }}>
            <Icon name="x" size={16} /> Reject
          </button>
          <button type="button" className={css.btnP} onClick={approveAll} disabled={approving || !pending.length}>
            <Icon name="check" size={16} /> Approve Timesheet
          </button>
        </div>
      </div>

      <Modal
        opened={rejecting}
        onClose={() => { setRejecting(false); setReason(""); }}
        title={`Reject ${row.name}'s timesheet`}
        centered
        radius={14}
        zIndex={500}
      >
        <Textarea
          label="Reason for rejection"
          description="Shared with the staff member so they can fix and resubmit."
          placeholder="e.g. Clock-out time looks incorrect. Please review and resubmit."
          minRows={3}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group position="right" mt={16}>
          <button type="button" className={css.btnG} onClick={() => { setRejecting(false); setReason(""); }}>Cancel</button>
          <button type="button" className={css.btnP} onClick={rejectAll} disabled={approving || !reason.trim()}>
            <Icon name="x" size={16} /> Reject timesheet
          </button>
        </Group>
      </Modal>
    </Drawer>
  );
}
